import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './PriceBook.css';

const StandardPricingForm = ({ 
  item, 
  onSave, 
  onCancel, 
  isEditing = false 
}) => {
  const [formData, setFormData] = useState({
    base_price: '',
    per_unit_price: '',
    setup_fee: '',
    tier_price: '',
    billing_frequency_id: '',
    is_active: true
  });
  
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFrequencies();
    if (isEditing && item) {
      fetchExistingPricing();
    }
  }, [isEditing, item]);

  const fetchFrequencies = async () => {
    try {
      const response = await api.get('/price-book/frequencies');
      setFrequencies(response.data);
    } catch (error) {
      console.error('Error fetching frequencies:', error);
    }
  };

  const fetchExistingPricing = async () => {
    try {
      const response = await api.get(`/price-book/items/${item.id}/standard-pricing`);
      const pricing = response.data;
      setFormData({
        base_price: pricing.base_price || '',
        per_unit_price: pricing.per_unit_price || '',
        setup_fee: pricing.setup_fee || '',
        tier_price: pricing.tier_price || '',
        billing_frequency_id: pricing.billing_frequency_id || '',
        is_active: pricing.is_active !== undefined ? pricing.is_active : true
      });
    } catch (error) {
      console.error('Error fetching existing pricing:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.billing_frequency_id) {
      setError('Billing frequency is required');
      return false;
    }

    const hasBasePrice = formData.base_price && parseFloat(formData.base_price) >= 0;
    const hasTierPrice = formData.tier_price && parseFloat(formData.tier_price) >= 0;
    
    if (!hasBasePrice && !hasTierPrice) {
      setError('Either base price or tier price is required');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const url = `/price-book/items/${item.id}/standard-pricing`;
      const method = isEditing ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      onSave(response.data);
    } catch (error) {
      console.error('Error saving pricing:', error);
      setError(error.response?.data?.error || 'Failed to save pricing');
    } finally {
      setLoading(false);
    }
  };

  const selectedFrequency = frequencies.find(f => f.id === parseInt(formData.billing_frequency_id));

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{isEditing ? 'Edit' : 'Add'} Standard Pricing</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="pricing-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-section">
            <h4>Item: {item?.item_name}</h4>
            <p className="item-description">{item?.description}</p>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="billing_frequency_id">
                Billing Frequency <span className="required">*</span>
              </label>
              <select
                id="billing_frequency_id"
                name="billing_frequency_id"
                value={formData.billing_frequency_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select frequency...</option>
                {frequencies.map(freq => (
                  <option key={freq.id} value={freq.id}>
                    {freq.display_label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section">
            <h4>Pricing Structure</h4>
            <p className="form-help">
              Choose either base pricing (fixed + per unit) or tier pricing.
            </p>
            
            <div className="pricing-options">
              <div className="pricing-option">
                <h5>Base Pricing</h5>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="base_price">Base Price ($)</label>
                    <input
                      type="number"
                      id="base_price"
                      name="base_price"
                      value={formData.base_price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="per_unit_price">Per Unit Price ($)</label>
                    <input
                      type="number"
                      id="per_unit_price"
                      name="per_unit_price"
                      value={formData.per_unit_price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="pricing-divider">OR</div>

              <div className="pricing-option">
                <h5>Tier Price</h5>
                <div className="form-group">
                  <label htmlFor="tier_price">
                    Tier Price ($)
                    {selectedFrequency && (
                      <span className="frequency-label">
                        per {selectedFrequency.display_label}
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    id="tier_price"
                    name="tier_price"
                    value={formData.tier_price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="setup_fee">Setup Fee ($)</label>
              <input
                type="number"
                id="setup_fee"
                name="setup_fee"
                value={formData.setup_fee}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
              <small className="form-help">One-time setup fee (optional)</small>
            </div>
          </div>

          {isEditing && (
            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active (available for use)
                </label>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')} Pricing
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StandardPricingForm; 